import { useMemo, useEffect, useRef } from "react";
import { Line } from "@react-three/drei";
import * as THREE from "three";

import { Pt } from "../../utils/apolloRenderer";
import { ApolloFrameApi } from "../../hooks/useApolloFrame";

/**
 * S5 — 경로류(z-up): 계획궤적(청록 리본) · 목표경로(빨강) · 지나온경로(회색) · 예측선(장애물별).
 * (Dreamview trajectory/routing/prediction.js 참조. 폴 주기로 갱신.)
 */

const OBSTACLE_COLOR: { [k: number]: string } = { 3: "#ff5a5a", 4: "#ffb302", 5: "#3aa0ff", 6: "#9aa0a6", 7: "#ff2d55" };
const obsCol = (t?: number) => (t != null && OBSTACLE_COLOR[t]) || "#a07bff";

const toXY = (pts: Pt[], z: number): [number, number, number][] => pts.map((p) => [p[0], p[1], z]);

// 폴리라인을 누적 거리 maxLen(m)까지만 잘라 예측선 과도 길이 방지
function clampPolyline(pts: Pt[], maxLen: number): Pt[] {
    if (!pts || pts.length < 2) return pts || [];
    const out: Pt[] = [pts[0]];
    let acc = 0;
    for (let i = 1; i < pts.length; i++) {
        const dx = pts[i][0] - pts[i - 1][0], dy = pts[i][1] - pts[i - 1][1];
        const seg = Math.hypot(dx, dy);
        if (acc + seg >= maxLen) { const f = seg > 0 ? (maxLen - acc) / seg : 0; out.push([pts[i - 1][0] + dx * f, pts[i - 1][1] + dy * f]); break; }
        acc += seg; out.push(pts[i]);
    }
    return out;
}

// 폴리라인 → 평평한 리본 지오메트리(끝이 각짐, 폭은 월드 미터). Dreamview drawThickBandFromPoints 방식.
function buildRibbon(pts: Pt[], width: number, z: number): THREE.BufferGeometry | null {
    if (!pts || pts.length < 2) return null;
    const half = width / 2, n = pts.length;
    const pos: number[] = [], idx: number[] = [];
    for (let i = 0; i < n; i++) {
        let dx = 0, dy = 0;
        if (i > 0) { dx += pts[i][0] - pts[i - 1][0]; dy += pts[i][1] - pts[i - 1][1]; }
        if (i < n - 1) { dx += pts[i + 1][0] - pts[i][0]; dy += pts[i + 1][1] - pts[i][1]; }
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len * half, ny = dx / len * half; // 진행방향 수직
        pos.push(pts[i][0] + nx, pts[i][1] + ny, z);   // 좌
        pos.push(pts[i][0] - nx, pts[i][1] - ny, z);   // 우
    }
    for (let i = 0; i < n - 1; i++) {
        const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2, d = (i + 1) * 2 + 1;
        idx.push(a, b, c, b, d, c);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setIndex(idx);
    return g;
}

function PlanningRibbon({ points, width, z, color }: { points: Pt[]; width: number; z: number; color: string }) {
    const geom = useMemo(() => buildRibbon(points, width, z), [points, width, z]);
    const prev = useRef<THREE.BufferGeometry | null>(null);
    useEffect(() => {
        prev.current = geom;
        return () => { geom?.dispose(); }; // 이전 지오메트리 해제(폴 주기 재생성 누수 방지)
    }, [geom]);
    if (!geom) return null;
    return (
        <mesh geometry={geom}>
            <meshBasicMaterial color={color} transparent opacity={0.85} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
    );
}

export default function Paths({ api, showPrediction = false, visible = true }: { api: ApolloFrameApi; showPrediction?: boolean; visible?: boolean }) {
    const f = api.latest;
    if (!visible) return null;
    const traveled = api.traveledRef.current;
    const planning = f?.planningTrajectory || [];
    const route = f?.routePath || [];
    const obstacles = f?.obstacles || [];

    return (
        <group>
            {/* 지나온 경로 */}
            {traveled.length >= 2 && (
                <Line points={toXY(traveled, 0.06)} color="#b4b9c3" lineWidth={2} transparent opacity={0.6} />
            )}
            {/* 목표 경로 */}
            {route.length >= 2 && (
                <Line points={toXY(route, 0.15)} color="#e74c3c" lineWidth={2.5} transparent opacity={0.85} />
            )}
            {/* 계획 궤적 (평평한 리본 — 끝이 각짐, 폭은 월드 미터) */}
            {planning.length >= 2 && (
                <PlanningRibbon points={planning} width={1.8} z={0.18} color="#00dcc8" />
            )}
            {/* 예측선(고확률만, 60m 클램프) */}
            {showPrediction && obstacles.map((o, oi) => (
                (o.predictions || []).map((p, pi) => {
                    if ((p.probability ?? 0) < 0.5) return null;
                    const pts = clampPolyline(p.points, 60);
                    if (pts.length < 2) return null;
                    return <Line key={"pred" + oi + "_" + pi} points={toXY(pts, 0.22)} color={obsCol(o.type)} lineWidth={2.6} dashed dashSize={1.2} gapSize={0.6} transparent opacity={0.95} />;
                })
            ))}
        </group>
    );
}
